import { supabaseDataProvider } from "ra-supabase-core";
import {
  withLifecycleCallbacks,
  type DataProvider,
  type GetListParams,
  type Identifier,
  type ResourceCallbacks,
} from "ra-core";
import type {
  ContactNote,
  CreateMcpAccessTokenInput,
  CreatedMcpAccessToken,
  Deal,
  DealNote,
  EmailDraft,
  MailAccount,
  MailConnectionInput,
  McpAccessToken,
  RAFile,
  Sale,
  SalesFormData,
  SendMailInput,
  SignUpData,
} from "../../types";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import { ATTACHMENTS_BUCKET } from "../commons/attachments";
import { getIsInitialized } from "./authProvider";
import { getSupabaseClient } from "./supabase";

const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";

const getBaseDataProvider = () =>
  supabaseDataProvider({
    instanceUrl: import.meta.env.VITE_SUPABASE_URL,
    apiKey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
    supabaseClient: getSupabaseClient(),
    sortOrder: "asc,desc.nullslast" as any,
  });

const processCompanyLogo = async (params: any) => {
  const logo = params.data.logo;

  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

const getDataProviderWithCustomMethods = () => {
  const baseDataProvider = getBaseDataProvider();

  const invokeMail = async <T>(body: Record<string, unknown>) => {
    const { data, error } = await getSupabaseClient().functions.invoke<{
      data: T;
    }>("mail", { method: "POST", body });

    if (!data || error) {
      let message = error?.message || "Mail operation failed";
      try {
        const details = await error?.context?.json();
        message = details?.message || message;
      } catch {
        // The response body is optional; keep the original function error.
      }
      throw new Error(message);
    }
    return data.data;
  };

  const invokeMcpTokenApi = async <T>(
    path = "",
    init: RequestInit = {},
  ): Promise<T> => {
    const {
      data: { session },
      error: sessionError,
    } = await getSupabaseClient().auth.getSession();

    if (!session?.access_token || sessionError) {
      throw new Error("Authentication required");
    }

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mcp/tokens${path}`,
      {
        ...init,
        headers: {
          apikey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
          ...init.headers,
        },
      },
    );

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.message || "MCP token operation failed");
    }

    return payload.data as T;
  };

  return {
    ...baseDataProvider,
    async getList(resource: string, params: GetListParams) {
      if (resource === "companies") {
        return baseDataProvider.getList("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getList("contacts_summary", params);
      }
      if (resource === "activity_log") {
        const { data, total } = await baseDataProvider.getList(
          "activity_log",
          params,
        );
        // Rename snake_case view columns to camelCase to match Activity type
        return {
          data: data.map((row: any) => ({
            ...row,
            contactNote: row.contact_note ?? undefined,
            dealNote: row.deal_note ?? undefined,
            contact_note: undefined,
            deal_note: undefined,
          })),
          total,
        };
      }

      return baseDataProvider.getList(resource, params);
    },
    async getOne(resource: string, params: any) {
      if (resource === "companies") {
        return baseDataProvider.getOne("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getOne("contacts_summary", params);
      }

      return baseDataProvider.getOne(resource, params);
    },

    async signUp({ email, password, first_name, last_name }: SignUpData) {
      const response = await getSupabaseClient().auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name,
            last_name,
          },
        },
      });

      if (!response.data?.user || response.error) {
        console.error("signUp.error", response.error);
        throw new Error(response?.error?.message || "Failed to create account");
      }

      window.localStorage.setItem(IS_INITIALIZED_CACHE_KEY, "true");

      return {
        id: response.data.user.id,
        email,
        password,
      };
    },
    async salesCreate(body: SalesFormData) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        data: Sale;
      }>("users", {
        method: "POST",
        body,
      });

      if (!data || error) {
        console.error("salesCreate.error", error);
        const errorDetails = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();
        throw new Error(errorDetails?.message || "Failed to create the user");
      }

      return data.data;
    },
    async salesUpdate(
      id: Identifier,
      data: Partial<Omit<SalesFormData, "password">>,
    ) {
      const { email, first_name, last_name, administrator, avatar, disabled } =
        data;

      const { data: updatedData, error } =
        await getSupabaseClient().functions.invoke<{
          data: Sale;
        }>("users", {
          method: "PATCH",
          body: {
            sales_id: id,
            email,
            first_name,
            last_name,
            administrator,
            disabled,
            avatar,
          },
        });

      if (!updatedData || error) {
        console.error("salesCreate.error", error);
        throw new Error("Failed to update account manager");
      }

      return updatedData.data;
    },
    async updatePassword(id: Identifier) {
      const { data: passwordUpdated, error } =
        await getSupabaseClient().functions.invoke<boolean>("update_password", {
          method: "PATCH",
          body: {
            sales_id: id,
          },
        });

      if (!passwordUpdated || error) {
        console.error("update_password.error", error);
        throw new Error("Failed to update password");
      }

      return passwordUpdated;
    },
    async mcpAccessTokensList(): Promise<McpAccessToken[]> {
      return invokeMcpTokenApi<McpAccessToken[]>();
    },
    async mcpAccessTokenCreate(
      input: CreateMcpAccessTokenInput,
    ): Promise<CreatedMcpAccessToken> {
      return invokeMcpTokenApi<CreatedMcpAccessToken>("", {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    async mcpAccessTokenRevoke(id: string): Promise<McpAccessToken> {
      return invokeMcpTokenApi<McpAccessToken>(`/${id}`, {
        method: "DELETE",
      });
    },
    async unarchiveDeal(deal: Deal) {
      // get all deals where stage is the same as the deal to unarchive
      const { data: deals } = await baseDataProvider.getList<Deal>("deals", {
        filter: { stage: deal.stage },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "index", order: "ASC" },
      });

      // set index for each deal starting from 1, if the deal to unarchive is found, set its index to the last one
      const updatedDeals = deals.map((d, index) => ({
        ...d,
        index: d.id === deal.id ? 0 : index + 1,
        archived_at: d.id === deal.id ? null : d.archived_at,
      }));

      return await Promise.all(
        updatedDeals.map((updatedDeal) =>
          baseDataProvider.update("deals", {
            id: updatedDeal.id,
            data: updatedDeal,
            previousData: deals.find((d) => d.id === updatedDeal.id),
          }),
        ),
      );
    },
    async isInitialized() {
      return getIsInitialized();
    },
    async mergeContacts(sourceId: Identifier, targetId: Identifier) {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "merge_contacts",
        {
          method: "POST",
          body: { loserId: sourceId, winnerId: targetId },
        },
      );

      if (error) {
        console.error("merge_contacts.error", error);
        throw new Error("Failed to merge contacts");
      }

      return data;
    },
    async getConfiguration(): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.getOne("configuration", {
        id: 1,
      });
      return (data?.config as ConfigurationContextValue) ?? {};
    },
    async updateConfiguration(
      config: ConfigurationContextValue,
    ): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.update("configuration", {
        id: 1,
        data: { config },
        previousData: { id: 1 },
      });
      return data.config as ConfigurationContextValue;
    },
    async mailAccountGet() {
      return invokeMail<MailAccount | null>({ action: "get_account" });
    },
    async mailAccountConnect(account: MailConnectionInput) {
      return invokeMail<MailAccount>({ action: "connect", account });
    },
    async mailAccountDisconnect() {
      return invokeMail<{ disconnected: boolean }>({ action: "disconnect" });
    },
    async mailSync() {
      return invokeMail<{ imported: number; last_synced_at: string }>({
        action: "sync",
      });
    },
    async mailDraftSave(message: SendMailInput, id?: string, version?: string) {
      return invokeMail<EmailDraft>({
        action: "save_draft",
        message,
        id,
        version,
      });
    },
    async mailDraftApprove(id: string, version: string, fromEmail: string) {
      return invokeMail({ action: "approve_draft", id, version, fromEmail });
    },
  } satisfies DataProvider;
};

export type CrmDataProvider = ReturnType<
  typeof getDataProviderWithCustomMethods
>;

const processConfigLogo = async (logo: any): Promise<string> => {
  if (typeof logo === "string") return logo;
  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo);
    return logo.src;
  }
  return logo?.src ?? "";
};

const lifeCycleCallbacks: ResourceCallbacks[] = [
  {
    resource: "configuration",
    beforeUpdate: async (params) => {
      const config = params.data.config;
      if (config) {
        config.lightModeLogo = await processConfigLogo(config.lightModeLogo);
        config.darkModeLogo = await processConfigLogo(config.darkModeLogo);
      }
      return params;
    },
  },
  {
    resource: "contact_notes",
    beforeSave: async (data: ContactNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "deal_notes",
    beforeSave: async (data: DealNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map((fi) => uploadToBucket(fi)),
        );
      }
      return data;
    },
  },
  {
    resource: "sales",
    beforeSave: async (data: Sale, _, __) => {
      if (data.avatar) {
        await uploadToBucket(data.avatar);
      }
      return data;
    },
  },
  {
    resource: "contacts",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "first_name",
        "last_name",
        "company_name",
        "title",
        "email",
        "phone",
        "background",
      ])(params);
    },
  },
  {
    resource: "companies",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "name",
        "phone_number",
        "website",
        "tax_identifier",
        "ornz",
        "ogrn",
        "research_email",
        "decision_maker",
        "decision_maker_role",
        "personalized_reason",
        "description",
        "zipcode",
        "city",
        "state_abbr",
      ])(normalizeCompanySizeFilter(params));
    },
    beforeCreate: async (params) => {
      const createParams = await processCompanyLogo(params);

      return {
        ...createParams,
        data: {
          created_at: new Date().toISOString(),
          ...createParams.data,
        },
      };
    },
    beforeUpdate: async (params) => {
      return await processCompanyLogo(params);
    },
  },
  {
    resource: "contacts_summary",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["first_name", "last_name"])(params);
    },
  },
  {
    resource: "deals",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "category", "description"])(params);
    },
  },
  {
    resource: "email_templates",
    beforeGetList: async (params) =>
      applyFullTextSearch(["name", "subject", "text"])({
        ...params,
        meta: {
          ...params.meta,
          columns: "id,name,subject,text,attachment_count,created_at",
        },
      }),
  },
  {
    resource: "audit_research_directory",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "name",
        "role_or_audience",
        "company_name",
        "company_ornz",
        "person_ornz",
        "certificate_number",
        "platform",
      ])(params);
    },
  },
];

export const getDataProvider = () => {
  if (import.meta.env.VITE_SUPABASE_URL === undefined) {
    throw new Error("Please set the VITE_SUPABASE_URL environment variable");
  }
  if (import.meta.env.VITE_SB_PUBLISHABLE_KEY === undefined) {
    throw new Error(
      "Please set the VITE_SB_PUBLISHABLE_KEY environment variable",
    );
  }
  return withLifecycleCallbacks(
    getDataProviderWithCustomMethods(),
    lifeCycleCallbacks,
  ) as CrmDataProvider;
};

const applyFullTextSearch = (columns: string[]) => (params: GetListParams) => {
  if (!params.filter || !Object.hasOwn(params.filter, "q")) {
    return params;
  }
  const { q, ...filter } = params.filter;
  // q is a UI-only search value, never a PostgREST column, even when empty.
  const searchPattern = getSearchPattern(q ?? "");

  if (!searchPattern) {
    return {
      ...params,
      filter,
    };
  }

  return {
    ...params,
    filter: {
      ...filter,
      "or@": `(${columns
        .map((column) => `${getSearchColumn(column)}.ilike.*${searchPattern}*`)
        .join(",")})`,
    },
  };
};

const getSearchColumn = (column: string) => {
  if (column === "email") return "email_fts";
  if (column === "phone") return "phone_fts";
  return column;
};

const getSearchPattern = (q: unknown) =>
  String(q)
    .trim()
    .split(/\s+/)
    .map((term) => term.replace(/[(),*]/g, "").trim())
    .filter(Boolean)
    .join("*");

const companySizeRanges: Record<number, { min: number; max?: number }> = {
  1: { min: 1, max: 3 },
  3: { min: 1, max: 3 },
  10: { min: 4, max: 10 },
  25: { min: 11, max: 25 },
  50: { min: 26, max: 50 },
  51: { min: 51 },
  250: { min: 51 },
  500: { min: 51 },
};

const normalizeCompanySizeFilter = (params: GetListParams): GetListParams => {
  const filter = { ...(params.filter ?? {}) };
  const legacySize = filter.size;
  const hasRange = filter["size@gte"] != null || filter["size@lte"] != null;

  delete filter.size;

  if (!hasRange && legacySize != null) {
    const range = companySizeRanges[Number(legacySize)];
    if (range) {
      filter["size@gte"] = range.min;
      filter["size@lte"] = range.max;
    }
  }

  return {
    ...params,
    filter,
  };
};

const uploadToBucket = async (fi: RAFile) => {
  if (!fi.src.startsWith("blob:") && !fi.src.startsWith("data:")) {
    // Sign URL check if path exists in the bucket
    if (fi.path) {
      const { error } = await getSupabaseClient()
        .storage.from(ATTACHMENTS_BUCKET)
        .createSignedUrl(fi.path, 60);

      if (!error) {
        return fi;
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src)
        .then((res) => {
          if (res.status !== 200) {
            return null;
          }
          return res.blob();
        })
        .catch(() => null)
    : fi.rawFile;

  if (dataContent == null) {
    // We weren't able to download the file from its src (e.g. user must be signed in on another website to access it)
    // or the file has no content (not probable)
    // In that case, just return it as is: when trying to download it, users should be redirected to the other website
    // and see they need to be signed in. It will then be their responsibility to upload the file back to the note.
    return fi;
  }

  const file = fi.rawFile;
  const fileParts = file.name.split(".");
  const fileExt = fileParts.length > 1 ? `.${file.name.split(".").pop()}` : "";
  const fileName = `${Math.random()}${fileExt}`;
  const filePath = `${fileName}`;
  const { error: uploadError } = await getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .upload(filePath, dataContent);

  if (uploadError) {
    console.error("uploadError", uploadError);
    throw new Error("Failed to upload attachment");
  }

  const { data } = getSupabaseClient()
    .storage.from(ATTACHMENTS_BUCKET)
    .getPublicUrl(filePath);

  fi.path = filePath;
  fi.src = data.publicUrl;

  // save MIME type
  const mimeType = file.type;
  fi.type = mimeType;

  return fi;
};
